import { Link } from 'react-router-dom';
import {
  AlertCircle,
  Archive,
  ArchiveRestore,
  BellOff,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleDashed,
  Clock3,
} from 'lucide-react';
import { Button } from '../ui/Button';
import { Pill, type PillTone } from '../ui/Pill';
import { ProgressBar } from '../ui/ProgressBar';
import { formatDate } from '../../lib/dateFmt';
import {
  onboardingPersonStatus,
  type OnboardingPerson,
  type OnboardingTaskSummary,
} from '../../hooks/onboardingOps';

/**
 * One person row on the onboarding dashboard, expandable to a per-task
 * breakdown. Tasks differ per person (templates, ad-hoc assignments),
 * so fixed columns like the coordinator's station grid don't fit;
 * instead the chevron opens the person's own checklist grouped by
 * what's outstanding vs done.
 *
 * Layout note: progress and the status pill sit in fixed-width slots.
 * With variable widths the flexible name cell absorbed the difference
 * and the progress column drifted between rows.
 */

type TaskStatus = OnboardingTaskSummary['status'];

const TASK_STATUS_META: Record<
  TaskStatus,
  { label: string; tone: PillTone; icon: typeof CheckCircle2 }
> = {
  pending: { label: 'To do', tone: 'neutral', icon: CircleDashed },
  changes_requested: { label: 'Changes requested', tone: 'warning', icon: AlertCircle },
  awaiting_review: { label: 'Awaiting review', tone: 'info', icon: Clock3 },
  complete: { label: 'Done', tone: 'success', icon: CheckCircle2 },
};

/** Outstanding-first so the chase list reads top to bottom. */
const GROUP_ORDER: TaskStatus[] = [
  'pending',
  'changes_requested',
  'awaiting_review',
  'complete',
];

export function DashboardPersonRow({
  person: p,
  selected,
  expanded,
  onToggleSelect,
  onToggleExpand,
  onToggleHidden,
  onToggleMuted,
  onResendInvite,
  onToggleFinished,
}: {
  person: OnboardingPerson;
  selected: boolean;
  /** Owned by the dashboard so Expand all / Collapse all can drive it. */
  expanded: boolean;
  onToggleSelect: () => void;
  onToggleExpand: () => void;
  onToggleHidden: () => void;
  onToggleMuted: () => void;
  onResendInvite: () => void;
  /** Finish (archive) an active person, or reopen a finished one. */
  onToggleFinished: () => void;
}) {
  const status = onboardingPersonStatus(p);
  const outstanding = p.total - p.done;

  return (
    <li>
      <div className="flex flex-wrap items-center gap-3 px-5 py-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          aria-label={`Select ${p.full_name}`}
        />
        <button
          type="button"
          onClick={onToggleExpand}
          disabled={p.tasks.length === 0}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Hide' : 'Show'} tasks for ${p.full_name}`}
          className="flex h-6 w-6 flex-none items-center justify-center rounded-token-sm text-fg-muted transition-colors hover:bg-surface-2 hover:text-fg disabled:opacity-30"
        >
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <div className="min-w-[14rem] flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-fg">{p.full_name}</span>
            {p.finished ? (
              <Pill tone="success" size="sm" icon={<Archive className="h-3 w-3" />}>
                Finished
              </Pill>
            ) : null}
            {p.on_mobilisation ? (
              <Pill tone="info" size="sm" icon={<CalendarDays className="h-3 w-3" />}>
                On a mobilisation
              </Pill>
            ) : null}
            {p.hidden ? <Pill tone="neutral" size="sm">Hidden</Pill> : null}
            {p.reminders_muted ? (
              <Pill tone="neutral" size="sm" icon={<BellOff className="h-3 w-3" />}>
                Reminders paused
              </Pill>
            ) : null}
          </div>
          <div className="text-xs text-fg-muted">
            {p.position ?? 'Role unknown'}
            {p.contract ? ` · ${p.contract}` : ''}
            {p.start_date ? ` · starts ${formatDate(p.start_date)}` : ''}
          </div>
        </div>
        <div className="w-44 flex-none">
          <div className="mb-1 flex justify-between text-xs text-fg-muted">
            <span>Progress</span>
            <span>{p.done}/{p.total}</span>
          </div>
          <ProgressBar value={p.done} total={Math.max(p.total, 1)} ariaLabel="Onboarding progress" />
        </div>
        <div className="w-32 flex-none">
          {status === 'no_tasks' ? (
            <Pill tone="neutral" size="sm">No tasks</Pill>
          ) : status === 'complete' ? (
            <Pill tone="success" size="sm">Complete</Pill>
          ) : status === 'awaiting_approval' ? (
            <Pill tone="info" size="sm">Pending approval</Pill>
          ) : status === 'in_progress' ? (
            <Pill tone="info" size="sm">{outstanding} outstanding</Pill>
          ) : (
            <Pill tone="neutral" size="sm">Not started</Pill>
          )}
        </div>
        <div className="ml-auto flex flex-none flex-wrap items-center gap-1">
          <Link to={`/onboarding/submissions/${p.id}`}>
            <Button variant="ghost" size="sm">
              Submissions
            </Button>
          </Link>
          {p.finished ? (
            <Button
              variant="secondary"
              size="sm"
              iconLeft={<ArchiveRestore className="h-4 w-4" />}
              onClick={onToggleFinished}
            >
              Reopen
            </Button>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={onToggleHidden}>
                {p.hidden ? 'Unhide' : 'Hide'}
              </Button>
              <Button variant="ghost" size="sm" onClick={onToggleMuted}>
                {p.reminders_muted ? 'Resume reminders' : 'Pause reminders'}
              </Button>
              <Button variant="ghost" size="sm" onClick={onResendInvite}>
                Resend invite
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconLeft={<Archive className="h-4 w-4" />}
                onClick={onToggleFinished}
              >
                Finish
              </Button>
            </>
          )}
        </div>
      </div>

      {expanded && p.tasks.length > 0 ? (
        <div className="space-y-1.5 border-t border-divider/10 bg-surface-2/40 py-2.5 pl-[3.75rem] pr-5">
          {GROUP_ORDER.map((s) => {
            const items = p.tasks.filter((t) => t.status === s);
            if (items.length === 0) return null;
            const meta = TASK_STATUS_META[s];
            const Icon = meta.icon;
            return (
              <div key={s} className="flex flex-wrap items-start gap-1.5">
                <span className="w-36 flex-none pt-0.5 text-xs font-medium text-fg-muted">
                  {meta.label} ({items.length})
                </span>
                <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
                  {items.map((t, i) => (
                    <Pill key={i} tone={meta.tone} size="sm" icon={<Icon className="h-3 w-3" />}>
                      {t.title}
                    </Pill>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </li>
  );
}
